import {
  CheckCircle2,
  FileSearch,
  FileText,
  Info,
  RefreshCw,
  Send
} from "lucide-react";
import { useMemo, useState, type FC } from "react";
import { toast } from "sonner";

import { LoadingState } from "@/components/brand";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { ListCard } from "@/components/ui/list-card";
import { MetaLine } from "@/components/ui/meta-line";
import { Spinner } from "@/components/ui/spinner";
import { StatusBadge } from "@/components/ui/status-badge";
import { queryClient } from "@/lib/query/queryClient";
import { cn } from "@/lib/utils";
import { answersIssue } from "../../../../api/types/draft";
import type { Artifact } from "../../../caseIde/types";
import { KeyFactsAPI, keyFactErrorText } from "../../api";

import {
  noticeBriefQueryKey,
  useNoticeBrief
} from "../../hooks/useNoticeBrief";
import {
  dueCountdown,
  NOTICE_LABEL,
  noticeProgress,
  withConfirmedIssues
} from "../../model";
import type {
  KeyFact,
  NoticeBriefData,
  NoticeBriefPayload,
  NoticeIssueBrief,
  NoticeProposal
} from "../../types";
import { IssueCard, issueGaps } from "./IssueCard";

/** `rerunning` while Rerun Failed runs (it otherwise holds one issue key). */
const FAILED = "failed";

const day = (iso?: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
      });
};

const Heading: FC<{ children: string; meta?: string | null }> = ({
  children,
  meta
}) => (
  <div className="flex items-baseline gap-2 px-1">
    <h3 className="text-base font-medium text-fg">{children}</h3>
    {meta ? <span className="text-sm text-fg-3">{meta}</span> : null}
  </div>
);

const Proposals: FC<{
  proposals: NoticeProposal[];
  busy: string | null;
  onResolve: (p: NoticeProposal, accept: boolean) => void;
}> = ({ proposals, busy, onResolve }) => (
  <ListCard tone="accent" aria-label="Files that may be a notice">
    {proposals.map((p) => (
      <div key={p.node_id} className="flex flex-wrap items-center gap-3 p-4">
        <FileSearch className="size-4 shrink-0 text-accent-seed" aria-hidden />
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-base text-fg">
            {p.title} may be the {NOTICE_LABEL[p.notice_type] ?? "notice"}
          </span>
          <span className="text-sm text-fg-3">{p.reason}</span>
        </div>
        <Button
          size="xs"
          disabled={busy === p.node_id}
          onClick={() => onResolve(p, true)}
        >
          It's the {NOTICE_LABEL[p.notice_type] ?? "Notice"}
        </Button>
        <Button
          size="xs"
          variant="ghost"
          disabled={busy === p.node_id}
          onClick={() => onResolve(p, false)}
        >
          Not a Notice
        </Button>
      </div>
    ))}
  </ListCard>
);

const Stat: FC<{ value: string; label: string; tone?: string }> = ({
  value,
  label,
  tone = "text-fg"
}) => (
  <div className="flex flex-col">
    <span className={cn("text-lg font-medium tabular-nums", tone)}>
      {value}
    </span>
    <span className="text-sm text-fg-3">{label}</span>
  </div>
);

const Summary: FC<{
  brief: NoticeBriefData;
  progress: { done: number; total: number; running: number; failed: number };
  confirming: boolean;
  rerunningFailed: boolean;
  requested: boolean;
  onConfirm: () => void;
  onRerunFailed: () => void;
  onRequestAll?: () => void;
  onOpen: (a: Artifact) => void;
}> = ({
  brief,
  progress,
  confirming,
  rerunningFailed,
  requested,
  onConfirm,
  onRerunFailed,
  onRequestAll,
  onOpen
}) => {
  const due = dueCountdown(brief.deadline.date);
  const unconfirmed = brief.issues.filter((i) => !i.confirmed).length;
  const ready = brief.issues.filter(
    (i) => i.our_position === "ready" || i.our_position === "procedural"
  ).length;
  const needEvidence = brief.issues.filter(
    (i) => i.our_position === "needs_evidence"
  ).length;
  const e = brief.eligibility;
  return (
    <ListCard aria-label={`${brief.label} summary`} data-testid="notice-brief">
      <div className="flex flex-col gap-3 p-4">
        <div className="flex flex-wrap items-start gap-3">
          <div className="flex min-w-0 flex-1 basis-60 flex-col gap-1">
            <MetaLine
              className="flex-wrap"
              parts={[
                brief.label,
                brief.form_number ? `Form ${brief.form_number}` : null,
                brief.classification,
                brief.service_center,
                brief.issue_date ? `issued ${day(brief.issue_date)}` : null,
                brief.receipt_number
              ]}
            />
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-xl font-medium text-fg">
                {brief.deadline.date
                  ? `Due ${day(brief.deadline.date)}`
                  : brief.title}
              </h2>
              <StatusBadge
                tone={due.tone}
                className="tabular-nums"
                data-testid="notice-due"
              >
                {due.text}
              </StatusBadge>
            </div>
            {brief.deadline.date ? (
              <span className="text-sm text-fg-3">{brief.title}</span>
            ) : null}
          </div>
          {onRequestAll ? (
            <Button
              variant="primary"
              size="sm"
              disabled={requested}
              onClick={onRequestAll}
            >
              <Send aria-hidden />
              {requested
                ? "Requested From Client"
                : "Request All Missing From Client"}
            </Button>
          ) : null}
        </div>
        {brief.issues.length ? (
          <div
            className="flex flex-wrap gap-x-9 gap-y-2"
            data-testid="notice-stats"
          >
            <Stat
              value={`${ready} of ${brief.issues.length}`}
              label="Issues Ready"
              tone="text-success"
            />
            <Stat
              value={String(needEvidence)}
              label="Need Evidence"
              tone={needEvidence ? "text-warn" : "text-fg"}
            />
            {e.rule ? (
              <Stat
                value={`${e.accepted} of ${e.required}`}
                label="Criteria Accepted"
              />
            ) : null}
          </div>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        <MetaLine
          className="flex-1 basis-60 flex-wrap"
          parts={[
            brief.deadline.date ? `Deadline ${brief.deadline.basis}` : null,
            brief.party.role === "self_petitioner"
              ? `self-petitioner ${brief.party.beneficiary ?? ""}`.trim()
              : brief.party.petitioner,
            progress.total
              ? `${progress.done} of ${progress.total} analyses done`
              : null,
            progress.failed ? `${progress.failed} couldn't run` : null
          ]}
        />
        {progress.running ? <Spinner className="size-3 text-fg-3" /> : null}
        {progress.failed ? (
          <Button
            size="xs"
            variant="secondary"
            disabled={rerunningFailed}
            onClick={onRerunFailed}
          >
            <RefreshCw
              aria-hidden
              className={rerunningFailed ? "animate-spin" : undefined}
            />
            Rerun Failed
          </Button>
        ) : null}
        <Button
          size="xs"
          variant="ghost"
          onClick={() =>
            onOpen({ kind: "file", id: brief.node_id, title: brief.label })
          }
        >
          <FileText aria-hidden /> Open {brief.label}
        </Button>
      </div>
      {brief.issues.length ? (
        <div className="flex flex-wrap items-center gap-3 px-4 py-3">
          {brief.confirmed ? (
            <span className="flex flex-1 items-center gap-1.5 text-base text-fg-2">
              <CheckCircle2 className="size-4 text-success" aria-hidden />
              You confirmed what USCIS asked. Drafting follows these issues.
            </span>
          ) : (
            <>
              <span className="flex-1 text-base text-fg-2">
                {unconfirmed === brief.issues.length
                  ? `Check the ${brief.issues.length} issue${
                      brief.issues.length === 1 ? "" : "s"
                    } read from the ${brief.label}, then confirm.${
                      progress.running
                        ? " Analyses are already running; the draft waits for this."
                        : ""
                    }`
                  : `${unconfirmed} issue${
                      unconfirmed === 1 ? "" : "s"
                    } still to confirm.`}
              </span>
              <Button
                variant="primary"
                size="sm"
                disabled={confirming}
                onClick={onConfirm}
              >
                {confirming ? <Spinner className="size-3" /> : null}
                Confirm What USCIS Asked
              </Button>
            </>
          )}
        </div>
      ) : null}
    </ListCard>
  );
};

const Flags: FC<{ flags: NoticeBriefData["flags"] }> = ({ flags }) =>
  flags.length ? (
    <ListCard
      tone={flags.some((f) => f.severity === "must") ? "warn" : "neutral"}
    >
      {flags.map((f) => (
        <div key={f.code} className="flex items-start gap-2 px-4 py-3">
          <Info
            className={
              f.severity === "must"
                ? "mt-0.5 size-3.5 shrink-0 text-warn"
                : "mt-0.5 size-3.5 shrink-0 text-fg-3"
            }
            aria-hidden
          />
          <span className="text-base text-fg">{f.message}</span>
        </div>
      ))}
    </ListCard>
  ) : null;

/**
 * How the response draft answers the brief: issues answered, what needs work
 * on the letter as a whole, and whether the brief has moved on since.
 */
export const ResponseScore: FC<{
  brief: NoticeBriefData;
  response: NoticeBriefPayload["response"];
  onOpen: (artifact: Artifact) => void;
}> = ({ brief, response, onOpen }) => {
  const card = response?.scorecard;
  if (!response || !card) return null;
  const answered = card.issues.filter(answersIssue).length;
  const behind =
    !!response.brief_fingerprint &&
    response.brief_fingerprint !== brief.fingerprint;
  return (
    <div className="flex flex-col gap-2" data-testid="response-score">
      <Heading meta={card.checked ? null : "coverage not checked"}>
        Response
      </Heading>
      <ListCard>
        <div className="flex flex-wrap items-center gap-2 px-4 py-3">
          <span className="min-w-0 flex-1 truncate text-base font-medium text-fg">
            {response.title}
          </span>
          <StatusBadge
            tone={answered === card.issues.length ? "success" : "warn"}
          >
            {answered} of {card.issues.length} Answered
          </StatusBadge>
          {behind ? <StatusBadge tone="warn">Brief Changed</StatusBadge> : null}
          <Button
            size="xs"
            variant="ghost"
            onClick={() =>
              onOpen({
                kind: "draft",
                id: response.draft_id,
                title: response.title
              })
            }
          >
            <FileText aria-hidden /> Open
          </Button>
        </div>
        {behind ? (
          <p className="px-4 py-3 text-base text-fg-3">
            The {brief.label} brief changed after this draft was written (an
            issue, or evidence mapped to one). Ask the agent to revise it.
          </p>
        ) : null}
        {card.findings.length ? (
          <ul className="flex flex-col gap-1 px-4 py-3">
            {card.findings.map((finding, i) => (
              <li key={i} className="text-base text-fg-2">
                {finding}
              </li>
            ))}
          </ul>
        ) : null}
      </ListCard>
    </div>
  );
};

const Settled: FC<{ brief: NoticeBriefData }> = ({ brief }) => {
  const e = brief.eligibility;
  const paths = brief.strategy?.paths.length ? brief.strategy.paths : e.paths;
  if (!brief.accepted.length && !e.rule && !paths.length) return null;
  return (
    <div className="flex flex-col gap-2">
      <Heading>Settled by USCIS</Heading>
      <ListCard>
        {brief.accepted.length ? (
          <div className="flex flex-col gap-1.5 px-4 py-3">
            <span className="text-sm text-fg-3">
              Accepted in the {brief.label}: acknowledge, don't re-argue
            </span>
            <ul className="flex flex-col gap-1">
              {brief.accepted.map((a) => (
                <li
                  key={a}
                  className="flex items-start gap-2 text-base text-fg"
                >
                  <CheckCircle2
                    className="mt-0.5 size-3.5 shrink-0 text-success"
                    aria-hidden
                  />
                  {a}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {e.rule ? (
          <div
            className="flex flex-col gap-1 px-4 py-3"
            data-testid="eligibility-math"
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-base font-medium text-fg">
                {e.accepted} of {e.required} Criteria
              </span>
              <StatusBadge tone={e.remaining ? "warn" : "success"}>
                {e.remaining ? `${e.remaining} More Needed` : "Count Met"}
              </StatusBadge>
              <span className="text-sm text-fg-3">{e.rule}</span>
            </div>
            {e.note ? <p className="text-base text-fg-2">{e.note}</p> : null}
          </div>
        ) : null}
        {paths.length ? (
          <div className="flex flex-col gap-1 px-4 py-3">
            <span className="text-sm text-fg-3">Paths to Approval</span>
            <ul className="flex list-disc flex-col gap-0.5 pl-5 text-base text-fg">
              {paths.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </ListCard>
    </div>
  );
};

const Strategy: FC<{
  brief: NoticeBriefData;
  memoDraftId?: string;
  onOpen: (a: Artifact) => void;
}> = ({ brief, memoDraftId, onOpen }) => {
  const s = brief.strategy;
  if (!s) return null;
  const running = s.state === "running" || s.state === "queued";
  if (!s.short_answer && !running)
    return (
      <p
        className="flex items-center gap-1.5 px-1 text-sm text-fg-3"
        data-testid="strategy-pending"
      >
        <Info className="size-3.5 shrink-0" aria-hidden />
        Strategy starts when every issue&apos;s analysis is in.
      </p>
    );
  return (
    <div className="flex flex-col gap-2">
      <Heading meta={s.state === "outdated" ? "outdated" : null}>
        Strategy
      </Heading>
      <ListCard>
        <div className="flex flex-col gap-1.5 px-4 py-3">
          {s.short_answer ? (
            <p className="text-base text-fg">{s.short_answer}</p>
          ) : (
            <span className="flex items-center gap-1.5 text-base text-fg-3">
              <Spinner className="size-3" />
              Weighing the issues together
            </span>
          )}
          {s.theories.length ? (
            <div className="flex flex-col gap-0.5">
              <span className="text-sm text-fg-3">Alternative Theories</span>
              <ul className="flex list-disc flex-col gap-0.5 pl-5 text-base text-fg">
                {s.theories.map((t) => (
                  <li key={t}>{t}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {s.miscounts.map((m) => (
            <span key={m} className="text-sm text-warn">
              The notice misstates: {m}
            </span>
          ))}
        </div>
        {memoDraftId && s.short_answer ? (
          <div className="px-4 py-2">
            <Button
              size="xs"
              variant="ghost"
              onClick={() =>
                onOpen({
                  kind: "draft",
                  id: memoDraftId,
                  title: `${brief.label} Strategy`
                })
              }
            >
              <FileText aria-hidden /> Open Strategy Memo
            </Button>
          </div>
        ) : null}
      </ListCard>
    </div>
  );
};

/**
 * The Notice Brief: the default Key Facts view on a notice case. What the
 * notice is and when it is due, what USCIS settled, the criteria math and
 * the strategy, then one card per issue in the notice's order. The attorney
 * confirms what USCIS asked here; the analyses run without waiting for it.
 */
export const NoticeBrief: FC<{
  individualId: string;
  facts: KeyFact[];
  /** The notice label for the empty state ("RFE", "NOID/NOIR"). */
  expecting: string;
  onOpen: (artifact: Artifact) => void;
  onPrompt?: (text: string) => void;
  onReveal?: (draftId: string, text: string) => void;
}> = ({ individualId, facts, expecting, onOpen, onPrompt, onReveal }) => {
  const { data, loading, error, refetch } = useNoticeBrief(individualId, true);
  const [confirming, setConfirming] = useState(false);
  const [busyProposal, setBusyProposal] = useState<string | null>(null);
  const [rerunning, setRerunning] = useState<string | null>(null);
  const [requestedAll, setRequestedAll] = useState(false);
  const brief = data?.brief ?? null;
  const progress = useMemo(
    () =>
      brief
        ? noticeProgress(facts, brief.notice_key)
        : { done: 0, total: 0, running: 0, failed: 0 },
    [facts, brief]
  );
  const argueAt = useMemo(() => {
    const order = brief?.strategy?.order ?? [];
    return new Map(order.map((key, i) => [key, i + 1]));
  }, [brief]);

  const confirm = async () => {
    if (!brief) return;
    setConfirming(true);
    try {
      const out = await KeyFactsAPI.confirmNotice(
        individualId,
        brief.notice_key
      );
      toast.success(out.message);
      queryClient.setQueryData<NoticeBriefPayload>(
        noticeBriefQueryKey(individualId),
        (old) => withConfirmedIssues(old, out.confirmed ?? [])
      );
      refetch();
    } catch (e) {
      toast.error(keyFactErrorText(e));
    } finally {
      setConfirming(false);
    }
  };

  const resolve = async (p: NoticeProposal, accept: boolean) => {
    setBusyProposal(p.node_id);
    try {
      await KeyFactsAPI.resolveNoticeProposal(
        individualId,
        p.node_id,
        accept,
        p.notice_type
      );
      toast.success(
        accept
          ? `Reading ${p.title} as the ${
              NOTICE_LABEL[p.notice_type] ?? "notice"
            }.`
          : `${p.title} is not a notice.`
      );
      refetch();
    } catch (e) {
      toast.error(keyFactErrorText(e));
    } finally {
      setBusyProposal(null);
    }
  };

  const rerun = async (key: string) => {
    setRerunning(key);
    try {
      const out = await KeyFactsAPI.act(individualId, key, {
        action: "rerun_analysis"
      });
      toast.success(out.message);
      refetch();
    } catch (e) {
      toast.error(keyFactErrorText(e));
    } finally {
      setRerunning(null);
    }
  };

  const rerunFailed = async () => {
    const failed = (brief?.issues ?? []).filter(
      (i) => i.analysis.state === "failed"
    );
    setRerunning(FAILED);
    try {
      const out = await Promise.allSettled(
        failed.map((i) =>
          KeyFactsAPI.act(individualId, i.key, { action: "rerun_analysis" })
        )
      );
      const errors = out.filter((r) => r.status === "rejected");
      if (errors.length) toast.error(keyFactErrorText(errors[0].reason));
      else
        toast.success(
          `Rerunning ${failed.length} analys${
            failed.length === 1 ? "is" : "es"
          }.`
        );
      refetch();
    } finally {
      setRerunning(null);
    }
  };

  const request = (issue: NoticeIssueBrief, gaps: string[]) =>
    onPrompt?.(
      `Ask the client for what the ${brief?.label ?? "notice"}'s "${
        issue.title
      }" issue still needs: ${gaps.join("; ")}. Draft the client request.`
    );

  const missing = (brief?.issues ?? [])
    .map((issue) => ({ issue, gaps: issueGaps(issue) }))
    .filter((m) => m.gaps.length);
  const requestAll = () => {
    onPrompt?.(
      `Ask the client for everything the ${
        brief?.label ?? "notice"
      } response still needs, in one request:\n${missing
        .map((m) => `- "${m.issue.title}": ${m.gaps.join("; ")}`)
        .join("\n")}\nDraft the client request.`
    );
    setRequestedAll(true);
  };

  if (error)
    return <p className="py-10 text-center text-base text-danger">{error}</p>;
  if (loading || !data)
    return (
      <LoadingState
        className="h-full"
        text={`Loading the ${expecting} brief…`}
      />
    );

  const proposals = data.proposals ?? [];
  if (!brief)
    return (
      <div className="flex flex-col gap-4">
        {proposals.length ? (
          <Proposals
            proposals={proposals}
            busy={busyProposal}
            onResolve={(p, a) => void resolve(p, a)}
          />
        ) : null}
        <EmptyState
          icon={FileSearch}
          title={`Upload the ${expecting}`}
          description={`Add the ${expecting} to Case Files. CaseBlink reads it, sets the response deadline and starts an analysis of every issue.`}
        />
      </div>
    );

  const responseId = data.response?.has_content
    ? data.response.draft_id
    : undefined;
  const scores = new Map(
    (data.response?.scorecard?.issues ?? []).map((s) => [s.key, s])
  );
  return (
    <div className="flex flex-col gap-4" data-testid="notice-brief-view">
      {proposals.length ? (
        <Proposals
          proposals={proposals}
          busy={busyProposal}
          onResolve={(p, a) => void resolve(p, a)}
        />
      ) : null}
      <Summary
        brief={brief}
        progress={progress}
        confirming={confirming}
        rerunningFailed={rerunning === FAILED}
        requested={requestedAll}
        onConfirm={() => void confirm()}
        onRerunFailed={() => void rerunFailed()}
        onRequestAll={onPrompt && missing.length ? requestAll : undefined}
        onOpen={onOpen}
      />
      <Flags flags={brief.flags} />
      <Settled brief={brief} />
      <Strategy
        brief={brief}
        memoDraftId={data.memos.notice_strategy}
        onOpen={onOpen}
      />
      <ResponseScore
        brief={brief}
        response={data.response ?? null}
        onOpen={onOpen}
      />
      <div className="flex flex-col gap-2">
        <Heading meta={`in the ${brief.label}'s order`}>Issues</Heading>
        {brief.issues.map((issue) => (
          <IssueCard
            key={issue.key}
            issue={issue}
            label={brief.label}
            noticeNodeId={brief.node_id}
            memoDraftId={data.memos[issue.key]}
            responseDraftId={responseId}
            score={scores.get(issue.key)}
            argueAt={argueAt.get(issue.key)}
            rerunning={rerunning === issue.key}
            onOpen={onOpen}
            onRerun={(key) => void rerun(key)}
            onRequest={onPrompt ? request : undefined}
            onReveal={onReveal}
          />
        ))}
      </div>
    </div>
  );
};
